import styled from 'styled-components';

import SectionHeader from '../ui/SectionHeader';
import Reveal from '../ui/Reveal';
import { translations } from '../../data/i18n';
import { getServiceText, services } from '../../data/services';

const Section = styled.section`
  padding: 98px 28px;
  background:
    radial-gradient(circle at top, rgba(221,161,47,0.10), transparent 38%),
    var(--cream);
`;

const Grid = styled.div`
  max-width: var(--container);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 22px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  position: relative;
  min-height: 430px;
  overflow: hidden;
  border-radius: 22px;
  background:
    linear-gradient(145deg, rgba(255,255,255,0.98), rgba(255,248,236,0.9));
  box-shadow: var(--shadow-soft);
  border: 1px solid rgba(221,161,47,0.12);
  display: flex;
  flex-direction: column;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    border-color 0.25s ease;

  &::before {
    content: '';
    position: absolute;
    right: -56px;
    top: -56px;
    width: 160px;
    height: 160px;
    border-radius: 999px;
    background:
      radial-gradient(circle, rgba(221,161,47,0.24), transparent 68%);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 24px 65px rgba(0,0,0,0.15);
    border-color: rgba(221,161,47,0.34);
  }
`;

const ImageWrap = styled.div`
  position: relative;
  height: 178px;
  overflow: hidden;
  background: var(--cream-soft);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  ${Card}:hover & img {
    transform: scale(1.045);
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 18px;
  bottom: 16px;
  z-index: 2;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: rgba(255,248,236,0.94);
  border: 1px solid rgba(221,161,47,0.28);
  box-shadow: 0 14px 28px rgba(0,0,0,0.14);
  font-size: 1.7rem;
`;

const CardBody = styled.div`
  position: relative;
  z-index: 1;
  padding: 28px 24px 28px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  margin: 0 0 10px;
  color: var(--black);
  font-size: 1.35rem;
  text-transform: uppercase;
`;

const Description = styled.p`
  margin: 0 0 20px;
  color: var(--muted);
  line-height: 1.55;
`;

const Meta = styled.div`
  color: var(--text);
  font-weight: 850;
  line-height: 1.5;
`;

const Link = styled.a`
  display: inline-flex;
  margin-top: auto;
  padding-top: 22px;
  color: var(--gold-dark);
  font-weight: 950;
  text-transform: uppercase;
  font-size: 0.85rem;
`;

function ServicesSection({ language }) {
  const t = translations[language].services;

  return (
    <Section id="services">
      <Reveal>
        <SectionHeader eyebrow={t.subtitle} title={t.title} />
      </Reveal>

      <Grid>
        {services.map((service, index) => {
          const serviceText = getServiceText(service, language);

          return (
            <Reveal key={service.id} delay={index * 90}>
              <Card>
                <ImageWrap>
                  <img src={service.image} alt={serviceText.imageAlt} loading="lazy" />
                  <Icon aria-hidden="true">{service.icon}</Icon>
                </ImageWrap>

                <CardBody>
                  <Title>{serviceText.title}</Title>
                  <Description>{serviceText.description}</Description>

                  <Meta>
                    {t.priceFrom} {serviceText.priceFrom}<br />
                    {serviceText.duration}
                  </Meta>

                  <Link href="#booking">{t.more} →</Link>
                </CardBody>
              </Card>
            </Reveal>
          );
        })}
      </Grid>
    </Section>
  );
}

export default ServicesSection;
